import React from 'react';
import { faSitemap } from '@fortawesome/free-solid-svg-icons';
import BlogPost from '../BlogPost';
// Create: src/assets/images/blogs/agents-process-coordination.jpg
import agentsProcessImg from '../../../assets/images/blogs/agents-process-coordination.jpg';

const AgentsProcessCoordination = () => (
    <BlogPost
        title="Agents Don't Need Coordination. Processes Do."
        subtitle="The unit of work isn't the agent - it's actually the process."
        category="Architecture"
        categoryIcon={faSitemap}
        color="#003366"
        date="February 2026"
        readTime="5 min read"
        image={agentsProcessImg}
        imageAlt="Process coordination - agents operating within bounded process spaces"
        ctaHeading="Want simpler agent architecture?"
        ctaText="Book a discovery call and we'll show you how process-bounded agents eliminate coordination complexity."
        seoDescription="Why coordinating business processes instead of agents creates massively simpler agentic architectures with robust handoffs."
        seoKeywords="agent coordination, process automation, domain-driven design, agentic AI, knowledge flywheel, Nebulyx"
        slug="agents-process-coordination"
    >
        <p>
            Context graphs and the semantic layer parallel is great - especially the
            question about how context graphs avoid the same fate.
        </p>

        <p>
            But it surfaced something interesting - a change from the usual "how do we
            coordinate agents" question:
        </p>

        <blockquote className="blog-post-quote">
            "For us the unit of work isn't the agent - it's actually the process."
        </blockquote>

        <h3>Flipping the Coordination Problem</h3>

        <p>
            Most agent architectures focus on coordinating agents. How do they share
            context? How do they align semantics? How do they pass information without drift?
        </p>

        <p>
            We've flipped this - we don't coordinate agents, we coordinate business processes.
        </p>

        <p>
            Any agent can act on any decision space. The process expects an input, has
            defined paths through it, and produces an output. The agents don't need
            coordination because the process boundaries are already separated.
        </p>

        <p>
            They just need robust handoffs to each other.
        </p>

        <p>
            That's a massively simpler problem - still a problem to solve, but fundamentally
            different from trying to orchestrate a swarm of agents sharing a universal
            context layer.
        </p>

        <h3>The Right Model for the Right Job</h3>

        <p>
            We don't need a probabilistic LLM to do all the heavy lifting.
        </p>

        <p>
            We use our ConsistentAI for knowledge (Pat. Pend.) and process grounding, and Data Object Graphs as the bounded decision
            space for execution.
        </p>

        <p>
            We have a Small Language Model for reasoning but it gets Consistentanswers from the
            rest of the system.
        </p>

        <p>
            That means we have much less of a job trying to control unruly probabilistic
            LLMs attempting to do everything.
        </p>

        <h3>Think DDD, But for AI</h3>

        <p>
            Marketing's "qualified" only needs meaning within that specific process space,
            not universally.
        </p>

        <p>
            The handover to Sales is just the right details as a data transfer object plus
            any relevant context into the Sales process space.
        </p>

        <p>
            This is Domain-Driven Design principles applied to agentic systems.
        </p>

        <p>
            Bounded contexts and explicit interfaces - with each domain owning its own
            semantics and even its own Knowledge Flywheel (LLM - i.e. no shared context
            at all).
        </p>

        <p>
            The institutional memory for agents isn't one shared brain. It's a jigsaw of
            specific process spaces that fit together at the seams.
        </p>

        <h3>The Shift</h3>

        <p>
            Clean boundaries with robust handoffs. Each process a learning system.
        </p>

        <p>
            This is the move from tech-driven "how do we coordinate agents" to how do we
            shape an organisation with agentic and AI-native business processes.
        </p>

        <p>
            A different discipline, different problem space, different architecture. 🎯
        </p>
    </BlogPost>
);

export default AgentsProcessCoordination;
